import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { CalendarDays, Search, FileDown, FileUp, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import useJobConfig from '../hooks/useJobConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gantt chart column widths by view mode (in pixels)
const GANTT_COLUMN_WIDTHS = {
  [ViewMode.Day]: 65,
  [ViewMode.Week]: 265,
  [ViewMode.Month]: 300,
};

// Scroll offset when centering "today" on screen
const SCROLL_CENTER_OFFSET = 100;

// Row height for Gantt chart
const GANTT_ROW_HEIGHT = 50;

// Header height for Gantt chart
const GANTT_HEADER_HEIGHT = 50;

// Default column width for Gantt chart
const GANTT_DEFAULT_COLUMN_WIDTH = 65;

function buildStatusSegments(job) {
  const plan = job.schedule_plan || [];
  const jobStart = new Date(job.start_date);
  const jobEnd = new Date(job.end_date);

  // Use schedule_plan if it has entries
  if (plan.length > 0) {
    return plan.map(seg => ({
      status: seg.status,
      label: seg.label,
      depends_on: seg.depends_on,
      start: new Date(seg.start_date + 'T00:00:00'),
      end: new Date(seg.end_date + 'T23:59:59')
    })).filter(seg => seg.end >= seg.start);
  }

  // Fallback: single segment with current status
  return [{
    status: job.status,
    start: jobStart,
    end: jobEnd
  }];
}

export default function Schedule() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManagement = user?.role === 'management';
  const { hexForStatus, labelForStatus, visibleStatuses } = useJobConfig();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [techJobMap, setTechJobMap] = useState({}); // Maps tech_id to job_ids
  const [viewMode, setViewMode] = useState(ViewMode.Day);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    customer: 'all',
    status: 'all',
    technician: 'all'
  });
  const ganttContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  const [exportOpen, setExportOpen] = useState(false);
  const [exportJobId, setExportJobId] = useState('');
  const [exporting, setExporting] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [importing, setImporting] = useState(false);
  const [applyJobId, setApplyJobId] = useState('');
  const [applyingId, setApplyingId] = useState(null);
  const templateFileRef = useRef(null);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/schedule-templates`);
      setTemplates(res.data);
    } catch { /* management only */ }
  };

  const handleExport = async () => {
    if (!exportJobId) { toast.error('Select a job to export'); return; }
    setExporting(true);
    try {
      const res = await axios.get(`${API}/jobs/${exportJobId}/msproject-export`, { responseType: 'blob' });
      const job = jobs.find(j => j.id === exportJobId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Job_${(job?.job_number || 'export').replace(/[^a-zA-Z0-9-_]/g, '')}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('MS Project file downloaded');
      setExportOpen(false);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/schedule-templates/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Template "${res.data.name}" imported (${res.data.tasks.length} tasks)`);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    if (!applyJobId) { toast.error('Select a job to apply the template to'); return; }
    setApplyingId(templateId);
    try {
      const res = await axios.post(`${API}/jobs/${applyJobId}/apply-template`, { template_id: templateId });
      toast.success(`Template applied — ${res.data.segments.length} phases scheduled${res.data.end_date_extended ? ' (job end date extended)' : ''}`);
      fetchJobs();
      setTemplatesOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply template');
    } finally {
      setApplyingId(null);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/schedule-templates/${templateId}`);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchCustomers();
    fetchTechnicians();
    fetchTechAssignments();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs?include_archived=true`);
      setJobs(response.data);
      // Default all jobs to collapsed
      const expanded = {};
      response.data.forEach(j => { expanded[j.id] = false; });
      setExpandedJobs(expanded);
    } catch (error) {
      toast.error('Failed to load jobs');
    }
  };

  const fetchTechAssignments = async () => {
    try {
      // Fetch all technicians and their assignments to build the mapping
      const techsRes = await axios.get(`${API}/technicians`);
      const techMap = {};
      
      // For each technician, fetch their assignments
      for (const tech of techsRes.data) {
        try {
          const assignRes = await axios.get(`${API}/assignments/tech/${tech.id}`);
          const jobIds = [...new Set(assignRes.data.map(a => a.job_id))];
          techMap[tech.id] = jobIds;
        } catch (e) {
          techMap[tech.id] = [];
        }
      }
      setTechJobMap(techMap);
    } catch (e) {
      console.error('Failed to load tech assignments', e);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (e) {
      console.error('Failed to load customers', e);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/technicians`);
      setTechnicians(response.data);
    } catch (e) {
      console.error('Failed to load technicians', e);
    }
  };

  // Helper to extract numeric part from job number for sorting
  const extractNumber = (jobNumber) => {
    const match = jobNumber.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  const filteredJobs = useMemo(() => {
    return jobs
      .filter(job => {
        // Search filter
        if (searchTerm) {
          const search = searchTerm.toLowerCase();
          const matchesSearch = 
            job.job_number.toLowerCase().includes(search) ||
            (job.customer_name && job.customer_name.toLowerCase().includes(search)) ||
            (job.make && job.make.toLowerCase().includes(search)) ||
            (job.model && job.model.toLowerCase().includes(search));
          if (!matchesSearch) return false;
        }
        
        if (filters.status === 'all' && job.status === 'completed') return false;
        if (filters.customer !== 'all') {
          if (job.customer_id && job.customer_id !== filters.customer) return false;
        }
        if (filters.status !== 'all' && job.status !== filters.status) return false;
        if (filters.technician !== 'all') {
          // Use techJobMap to check if this technician is assigned to this job
          const techJobs = techJobMap[filters.technician] || [];
          if (!techJobs.includes(job.id)) return false;
        }
        return true;
      })
      // Sort by job number (numeric part) - lowest first
      .sort((a, b) => extractNumber(a.job_number) - extractNumber(b.job_number));
  }, [jobs, filters, techJobMap, searchTerm]);

  const tasks = useMemo(() => {
    const result = [];

    filteredJobs.forEach(job => {
      const segments = buildStatusSegments(job);
      const hasMultipleSegments = segments.length > 1;
      const isExpanded = expandedJobs[job.id] === true;

      if (hasMultipleSegments) {
        // Parent project task
        result.push({
          id: job.id,
          name: `${job.job_number} - ${job.customer_name}`,
          start: new Date(job.start_date),
          end: new Date(job.end_date),
          progress: job.status === 'completed' ? 100 : 0,
          type: 'project',
          isExpanded: isExpanded,
          hideChildren: !isExpanded,
          styles: {
            backgroundColor: hexForStatus(job.status),
            backgroundSelectedColor: hexForStatus(job.status),
            progressColor: hexForStatus(job.status),
            progressSelectedColor: hexForStatus(job.status)
          }
        });

        // Child segment tasks
        segments.forEach((seg, idx) => {
          result.push({
            id: `${job.id}_seg_${idx}`,
            name: seg.label || labelForStatus(seg.status),
            dependencies: (seg.depends_on || [])
              .filter(d => d >= 0 && d < segments.length && d !== idx)
              .map(d => `${job.id}_seg_${d}`),
            start: seg.start,
            end: seg.end,
            progress: 100,
            type: 'task',
            project: job.id,
            styles: {
              backgroundColor: hexForStatus(seg.status),
              backgroundSelectedColor: hexForStatus(seg.status),
              progressColor: hexForStatus(seg.status),
              progressSelectedColor: hexForStatus(seg.status)
            }
          });
        });
      } else {
        // Single segment - just a regular task
        const color = hexForStatus(job.status);
        result.push({
          id: job.id,
          name: `${job.job_number} - ${job.customer_name}`,
          start: new Date(job.start_date),
          end: new Date(job.end_date),
          progress: 100,
          type: 'task',
          styles: {
            backgroundColor: color,
            backgroundSelectedColor: color,
            progressColor: color,
            progressSelectedColor: color
          }
        });
      }
    });

    return result;
  }, [filteredJobs, expandedJobs, hexForStatus, labelForStatus]);

  const handleTaskClick = (task) => {
    // Extract the real job id (strip _seg_N suffix if present)
    const jobId = task.id.includes('_seg_') ? task.id.split('_seg_')[0] : task.id;
    navigate(`/jobs/${jobId}`);
  };

  const handleExpanderClick = (task) => {
    if (task.type === 'project') {
      setExpandedJobs(prev => ({ ...prev, [task.id]: !prev[task.id] }));
    }
  };

  // Helper function to find the horizontally scrollable element in the Gantt
  const getScrollableElement = useCallback(() => {
    if (!ganttContainerRef.current) return null;
    
    // The gantt-task-react library creates a specific structure
    // Look for the grid wrapper which is the main scrollable container
    const allDivs = ganttContainerRef.current.querySelectorAll('div');
    let bestMatch = null;
    let maxScrollWidth = 0;
    
    for (const div of allDivs) {
      // Find the div with the largest scrollable area
      const hasHorizontalScroll = div.scrollWidth > div.clientWidth + 20;
      if (hasHorizontalScroll && div.scrollWidth > maxScrollWidth) {
        maxScrollWidth = div.scrollWidth;
        bestMatch = div;
      }
    }
    
    return bestMatch;
  }, []);

  // Scroll to today - used by both auto-scroll and button
  const scrollToToday = useCallback(() => {
    if (!ganttContainerRef.current || tasks.length === 0) return;
    
    // Find scrollable element
    const scrollableEl = getScrollableElement();
    if (!scrollableEl) {
      return;
    }
    
    // Calculate scroll position
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const minDate = tasks.reduce((min, t) => {
      const taskStart = new Date(t.start);
      taskStart.setHours(0, 0, 0, 0);
      return taskStart < min ? taskStart : min;
    }, new Date(tasks[0].start));
    
    const daysDiff = Math.round((today - minDate) / (1000 * 60 * 60 * 24));
    
    // Get column width based on current view mode
    const columnWidth = GANTT_COLUMN_WIDTHS[viewMode] || GANTT_DEFAULT_COLUMN_WIDTH;
    
    // Calculate target scroll position (center today on screen)
    const viewportCenter = scrollableEl.clientWidth / 2;
    const targetPosition = Math.max(0, (daysDiff * columnWidth) - viewportCenter + SCROLL_CENTER_OFFSET);
    
    scrollableEl.scrollTo({ left: targetPosition, behavior: 'smooth' });
  }, [getScrollableElement, tasks, viewMode]);

  // Auto-scroll to today on initial load and when view mode changes
  useEffect(() => {
    if (tasks.length === 0) return;
    
    // Wait for the Gantt chart to fully render/re-render after view change
    const timer = setTimeout(() => {
      scrollToToday();
    }, 800);
    
    return () => clearTimeout(timer);
  }, [tasks.length, viewMode, scrollToToday]);

  // Mouse drag/pan handlers
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.task-list-wrapper, button, select, input')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    const scrollableEl = getScrollableElement();
    if (scrollableEl) {
      setScrollStart({ x: scrollableEl.scrollLeft, y: scrollableEl.scrollTop });
    }
  }, [getScrollableElement]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const scrollableEl = getScrollableElement();
    if (scrollableEl) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      scrollableEl.scrollLeft = scrollStart.x - dx;
      scrollableEl.scrollTop = scrollStart.y - dy;
    }
  }, [isDragging, dragStart, scrollStart, getScrollableElement]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseUp, handleMouseMove]);

  return (
    <DashboardLayout>
      <div data-testid="schedule-page" className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Shop Schedule</h3>
            <p className="text-slate-600">Gantt view of all active jobs - click a job bar to view details. Expand rows to see status timeline.</p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {isManagement && (
              <>
                <Button
                  onClick={() => { setExportJobId(''); setExportOpen(true); }}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="msproject-export-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Export MS Project
                </Button>
                <Button
                  onClick={() => { setApplyJobId(''); setTemplatesOpen(true); fetchTemplates(); }}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="msproject-templates-btn"
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  Schedule Templates
                </Button>
              </>
            )}
            <Button
              onClick={scrollToToday}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="today-button"
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              Today
            </Button>
            {[ViewMode.Day, ViewMode.Week, ViewMode.Month].map(mode => (
              <Button
                key={mode}
                onClick={() => setViewMode(mode)}
                variant={viewMode === mode ? 'default' : 'outline'}
                className={viewMode === mode ? 'bg-slate-900 hover:bg-slate-800 text-white' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}
                data-testid={`view-${mode.toLowerCase()}`}
              >
                {mode === ViewMode.Day ? 'Day' : mode === ViewMode.Week ? 'Week' : 'Month'}
              </Button>
            ))}
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="p-6 bg-white border border-slate-200 shadow-sm">
          <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Search & Filters</h4>
          
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                type="text"
                placeholder="Search by job #, customer, make, model..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="schedule-search"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customer-filter">Customer</Label>
              <Select value={filters.customer} onValueChange={(value) => setFilters({ ...filters, customer: value })}>
                <SelectTrigger id="customer-filter" data-testid="customer-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status-filter">Status</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                <SelectTrigger id="status-filter" data-testid="status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Active Jobs</SelectItem>
                  {visibleStatuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tech-filter">Technician</Label>
              <Select value={filters.technician} onValueChange={(value) => setFilters({ ...filters, technician: value })}>
                <SelectTrigger id="tech-filter" data-testid="tech-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Technicians</SelectItem>
                  {technicians.map(tech => (
                    <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {tasks.length > 0 ? (
          <div ref={ganttContainerRef} className="bg-white border border-slate-200 rounded-md shadow-sm gantt-container" style={{ height: '600px', overflowY: 'auto', overflowX: 'auto' }}>
            <Gantt
              tasks={tasks}
              viewMode={viewMode}
              todayColor="rgba(59, 130, 246, 0.15)"
              onDoubleClick={handleTaskClick}
              onClick={handleTaskClick}
              onExpanderClick={handleExpanderClick}
              TooltipContent={({ task }) => {
                const isSegment = task.id.includes('_seg_');
                return (
                  <div className="bg-slate-900 text-white p-2 rounded text-xs">
                    <div className="font-bold">{task.name}</div>
                    <div>{new Date(task.start).toLocaleDateString()} - {new Date(task.end).toLocaleDateString()}</div>
                    {isSegment && (
                      <div className="flex items-center gap-1 mt-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: task.styles?.backgroundColor }}></div>
                        <span>Status segment</span>
                      </div>
                    )}
                    <div className="text-slate-300 mt-1">Click to view details</div>
                  </div>
                );
              }}
              listCellWidth="220px"
              columnWidth={GANTT_COLUMN_WIDTHS[viewMode] || GANTT_DEFAULT_COLUMN_WIDTH}
              barCornerRadius={4}
              barFill={60}
              handleWidth={8}
              fontSize="12px"
              fontFamily="Manrope, sans-serif"
              rowHeight={GANTT_ROW_HEIGHT}
              arrowColor="#94A3B8"
            />
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-12 text-center">
            <p className="text-slate-600">No jobs match the selected filters</p>
          </div>
        )}

        {/* Legend */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6">
          <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Status Legend</h4>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
            {visibleStatuses.map((s) => (
              <div key={s.value} className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: hexForStatus(s.value) }}></div>
                <span className="text-sm text-slate-700">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Export to MS Project dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md" data-testid="msproject-export-dialog">
          <DialogHeader>
            <DialogTitle>Export to MS Project</DialogTitle>
            <DialogDescription>
              Downloads a Microsoft Project XML (.xml) file with the job's schedule phases, durations, and dependencies.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Job</Label>
              <Select value={exportJobId} onValueChange={setExportJobId}>
                <SelectTrigger data-testid="msproject-export-job-select"><SelectValue placeholder="Select a job..." /></SelectTrigger>
                <SelectContent>
                  {[...jobs].sort((a, b) => extractNumber(a.job_number) - extractNumber(b.job_number)).map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.job_number} — {j.customer_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleExport} disabled={exporting || !exportJobId} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="msproject-export-download">
              <FileDown className="w-4 h-4 mr-2" /> {exporting ? 'Generating...' : 'Download .xml'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule templates dialog */}
      <Dialog open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="msproject-templates-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Templates</DialogTitle>
            <DialogDescription>
              Import a Microsoft Project XML file as a reusable template, then apply it to a job. Applying replaces the job's phase plan, anchored at the job's start date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 min-w-0">
            <input type="file" accept=".xml,text/xml,application/xml" ref={templateFileRef} className="hidden" onChange={handleImportFile} data-testid="msproject-import-input" />
            <Button variant="outline" className="w-full border-dashed" onClick={() => templateFileRef.current?.click()} disabled={importing} data-testid="msproject-import-btn">
              <FileUp className="w-4 h-4 mr-2" /> {importing ? 'Importing...' : 'Import Microsoft Project XML'}
            </Button>

            <div className="space-y-2">
              <Label>Apply to job</Label>
              <Select value={applyJobId} onValueChange={setApplyJobId}>
                <SelectTrigger data-testid="msproject-apply-job-select"><SelectValue placeholder="Select a job..." /></SelectTrigger>
                <SelectContent>
                  {[...jobs].sort((a, b) => extractNumber(a.job_number) - extractNumber(b.job_number)).map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.job_number} — {j.customer_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {templates.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No templates yet — import a Microsoft Project XML file above.</p>
            ) : (
              <div className="space-y-2">
                {templates.map(t => (
                  <div key={t.id} className="border border-slate-200 rounded-md p-3 flex items-center gap-3 min-w-0" data-testid={`schedule-template-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800 truncate" title={t.name}>{t.name}</p>
                      <p className="text-xs text-slate-500 truncate">{t.tasks.length} tasks · {new Date(t.created_at).toLocaleDateString()} · {t.created_by_name}</p>
                    </div>
                    <Button size="sm" onClick={() => handleApplyTemplate(t.id)} disabled={applyingId === t.id || !applyJobId} className="bg-slate-900 hover:bg-slate-800 shrink-0" data-testid={`apply-template-${t.id}`}>
                      {applyingId === t.id ? 'Applying...' : 'Apply'}
                    </Button>
                    <button onClick={() => handleDeleteTemplate(t.id)} className="text-slate-400 hover:text-red-500 shrink-0" data-testid={`delete-template-${t.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
